import { useState } from 'react';
import { Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomCellRendererProps } from 'ag-grid-react';
import { CellEditRequestEvent, ColDef, ColumnResizedEvent, ValueFormatterParams } from 'ag-grid-community';
import { InfniteScrollingGrid } from '../../AgGrid/InfiniteScrollingGrid';
import {
  getCogoPointSetting,
  searchCogoPointSettings,
  searchPropertyMappingsForCogoPoints,
} from '../../../api/arcgis-civil3d-cogo-point-setting.api';
import {
  useCreatePropertyMappingsForCogoPoints,
  useDeleteCogoPointSettings,
  useDuplicateCogoPointSettings,
  useUpdateCogoPointSetting,
} from '../../../hooks/api/use-arcgis-civil3d-cogo-point-settings.hook';
import { civil3dFeatureTypesByCategory, Civil3dFeatureType } from '../../../../../types/constants/civil3d-feature-type';
import { ArrayFilter } from '../../AgGrid/ArrayFilter';
import { useSearchPointStyles } from '../../../hooks/api/use-point-styles.hook';
import { useSearchPointLabelStyles } from '../../../hooks/api/use-point-label-styles.hook';
import {
  ArcgisCivil3dCogoPointSettingApiModel,
  ArcgisCivil3dCogoPointSettingRelationship,
  WithPointLabelStyle,
  WithPointStyle,
} from '../../../../../types/models/arcgis-civil3d-cogo-point-setting.model';
import { ArcgisCivil3dConfigurationGrid } from '../ArcgisCivil3dConfigurationGrid';
import {
  WithArcgisFeatureClass,
  WithAutodeskLayer,
  WithConfiguration,
} from '../../../../../types/models/arcgis-civil3d-setting.model';

export const CogoPointConfigurationGrid = () => {
  const navigate = useNavigate();
  const { configurationSettingId } = useParams<{ configurationSettingId: string }>();
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const [updateCogoPointSetting, { error: updateError }] = useUpdateCogoPointSetting();
  const [deleteCogoPointSettings, { error: deleteError }] = useDeleteCogoPointSettings();
  const [duplicateCogoPointSettings, { error: duplicateError }] = useDuplicateCogoPointSettings();
  const [createPropertyMappings, { error: createPropertyMappingsError }] = useCreatePropertyMappingsForCogoPoints();

  const { loading: loadingPointStyles, response: pointStylesResponse } = useSearchPointStyles({
    limit: 999999,
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });
  const { loading: loadingPointLabelStyles, response: pointLabelStylesResponse } = useSearchPointLabelStyles({
    limit: 999999,
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });

  const pointStyleIdNameMap = pointStylesResponse?.pages
    .flatMap(({ result }) => result)
    .reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {} as Record<string, string>);

  const pointLabelStyleIdNameMap = pointLabelStylesResponse?.pages
    .flatMap(({ result }) => result)
    .reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {} as Record<string, string>);

  const columns: ColDef<
    ArcgisCivil3dCogoPointSettingApiModel &
      WithConfiguration &
      WithArcgisFeatureClass &
      WithAutodeskLayer &
      WithPointStyle &
      WithPointLabelStyle & { pointGroup: string }
  >[] = [
    {
      headerName: 'Style',
      field: 'pointStyle',
      colId: 'pointStyle.name',
      width: columnWidths['pointStyle.name'] ? Number(columnWidths['pointStyle.name']) : 200,
      flex: 0,
      valueGetter: ({ data }) => data?.pointStyle?.id,
      editable: true,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: pointStylesResponse?.pages.flatMap(({ result }) => result).map(({ id }) => id) ?? [],
        formatValue: (value: number) => pointStyleIdNameMap?.[value] ?? '',
      },
      valueFormatter: (params: ValueFormatterParams<ArcgisCivil3dCogoPointSettingApiModel>) =>
        pointStyleIdNameMap?.[params.value] ?? '',
    },
    {
      headerName: 'Label Style',
      field: 'pointLabelStyle',
      colId: 'pointLabelStyle.name',
      width: columnWidths['pointLabelStyle.name'] ? Number(columnWidths['pointLabelStyle.name']) : 200,
      flex: 0,
      valueGetter: ({ data }) => data?.pointLabelStyle?.id,
      editable: true,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: pointLabelStylesResponse?.pages.flatMap(({ result }) => result).map(({ id }) => id) ?? [],
        formatValue: (value: number) => pointLabelStyleIdNameMap?.[value] ?? '',
      },
      valueFormatter: (params: ValueFormatterParams<ArcgisCivil3dCogoPointSettingApiModel>) =>
        pointLabelStyleIdNameMap?.[params.value] ?? '',
    },
    {
      headerName: 'Point Group',
      field: 'pointGroup',
      valueGetter: () => '_All Points',
      width: columnWidths.pointGroup ? Number(columnWidths.pointGroup) : 150,
      flex: 0,
    },
    {
      headerName: 'Feature Type',
      field: 'civil3dFeatureType',
      colId: 'civil3dFeatureType.name',
      width: columnWidths['civil3dFeatureType.name'] ? Number(columnWidths['civil3dFeatureType.name']) : 200,
      flex: 0,
      valueGetter: ({ data }: { data?: ArcgisCivil3dCogoPointSettingApiModel }) => data?.civil3dFeatureType,
      filter: ArrayFilter,
      editable: true,
      sortable: true,
      filterParams: { values: civil3dFeatureTypesByCategory[Civil3dFeatureType.COGO_POINT] },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: civil3dFeatureTypesByCategory[Civil3dFeatureType.COGO_POINT],
      },
    },
    {
      headerName: 'Property Mappings',
      width: columnWidths.propertyMappings ? Number(columnWidths.propertyMappings) : 250,
      flex: 0,
      cellRenderer: ({ node }: CustomCellRendererProps<ArcgisCivil3dCogoPointSettingApiModel>) => (
        <Button variant="text" onClick={() => navigate(`${node.id}/property-mappings`)} sx={{ textTransform: 'none' }}>
          View property mapping
        </Button>
      ),
    },
  ];

  const onColumnResized = (params: ColumnResizedEvent) => {
    setColumnWidths((prevColumnWidths) =>
      params.column
        ? {
            ...(prevColumnWidths ?? {}),
            [params.column.getColId()]: params.column.getActualWidth(),
          }
        : prevColumnWidths,
    );
  };

  const getCogoPointSettings: Parameters<
    typeof InfniteScrollingGrid<
      ArcgisCivil3dCogoPointSettingApiModel &
        WithConfiguration &
        WithArcgisFeatureClass &
        WithAutodeskLayer &
        WithPointStyle &
        WithPointLabelStyle
    >
  >[0]['onGetRows'] = async ({ cursor, limit, filterQuery, sortQuery }) => {
    const { data } = await searchCogoPointSettings<
      ArcgisCivil3dCogoPointSettingApiModel &
        WithConfiguration &
        WithArcgisFeatureClass &
        WithAutodeskLayer &
        WithPointStyle &
        WithPointLabelStyle
    >({
      ...filterQuery,
      sort: sortQuery,
      cursor,
      limit,
      include: [
        ArcgisCivil3dCogoPointSettingRelationship.AUTODESK_LAYER,
        ArcgisCivil3dCogoPointSettingRelationship.ARCGIS_FEATURE_CLASS,
        ArcgisCivil3dCogoPointSettingRelationship.CONFIGURATION,
        ArcgisCivil3dCogoPointSettingRelationship.POINT_LABEL_STYLE,
        ArcgisCivil3dCogoPointSettingRelationship.POINT_STYLE,
      ],
      configurationId: Number(configurationSettingId),
    });

    return data;
  };

  const updateSetting = async ({ colDef, data, newValue }: CellEditRequestEvent) => {
    if (!colDef?.field || !data?.id || colDef.field === 'query') return;

    const relationFieldMap = {
      autodeskLayer: 'autodeskLayerId',
      arcgisFeatureClass: 'arcgisFeatureClassId',
      lineStyle: 'lineStyleId',
      pointStyle: 'pointStyleId',
      pointLabelStyle: 'pointLabelStyleId',
    };

    const fieldKey =
      colDef.field in relationFieldMap ? relationFieldMap[colDef.field as keyof typeof relationFieldMap] : colDef.field;

    await updateCogoPointSetting({ cogoPointId: data.id, [fieldKey]: newValue });
  };

  const handleBulkDuplicatePropertyMappingsRecursive = async (
    originalDuplicateIdMap: Record<number, number>,
    idsToDuplicate: number[],
    cursor = 0,
  ) => {
    const { data } = await searchPropertyMappingsForCogoPoints({
      cogoPointIds: idsToDuplicate,
      limit: 500,
      cursor,
    });

    const propertyMappingsToDuplicate = data.result.map(({ setting, attribute, civil3dProperty }) => ({
      cogoPointSettingId: originalDuplicateIdMap[setting!.id],
      attributeId: attribute.id,
      civil3dPropertyId: civil3dProperty.id,
    }));
    if (propertyMappingsToDuplicate.length > 0) {
      await createPropertyMappings({ propertyMappings: propertyMappingsToDuplicate });
    }

    if (data.nextCursor) {
      await handleBulkDuplicatePropertyMappingsRecursive(originalDuplicateIdMap, idsToDuplicate, data.nextCursor);
    }
  };

  return (
    <ArcgisCivil3dConfigurationGrid
      featureType={Civil3dFeatureType.COGO_POINT}
      error={updateError || deleteError || duplicateError || createPropertyMappingsError}
      loading={loadingPointStyles || loadingPointLabelStyles}
      columns={columns}
      getSetting={async (cogoPointId) => {
        const { data } = await getCogoPointSetting({
          cogoPointId,
          include: [
            ArcgisCivil3dCogoPointSettingRelationship.CONFIGURATION,
            ArcgisCivil3dCogoPointSettingRelationship.ARCGIS_FEATURE_CLASS,
          ],
        });
        return data;
      }}
      onUpdateQuery={async (cogoPointId, query) => {
        await updateCogoPointSetting({ cogoPointId, query });
      }}
      onGetRows={getCogoPointSettings}
      onUpdateRow={updateSetting}
      onColumnResized={onColumnResized}
      onBulkDelete={async (settingIds) => {
        await deleteCogoPointSettings({ cogoPointIds: settingIds });
      }}
      onBulkDuplicate={async (settingIds) => {
        const duplicatedRecords = await duplicateCogoPointSettings({ cogoPointIds: settingIds });
        await handleBulkDuplicatePropertyMappingsRecursive(
          duplicatedRecords.reduce(
            (acc, { id, originalId }) => {
              if (!originalId) return acc;
              return { ...acc, [originalId]: id };
            },
            {} as Record<number, number>,
          ),
          settingIds,
        );
      }}
    />
  );
};
